import dayjs from "dayjs";

// Definitions for available placeholders within the remittance template.
// Each token exposes a key (used inside the template as {{key}}),
// a german label for the UI and a sample value for previews.
export const REMITTANCE_TOKENS = [
  {
    key: "book_month",
    label: "Buchungsmonat",
    description: "Monat der SEPA-Ausführung (MM/JJJJ)",
    sample: dayjs().format("MM/YYYY"),
  },
  {
    key: "next_payed_till",
    label: "Gezahlt bis (neu)",
    description: "Monat bis zu dem nach dem Einzug als bezahlt gilt",
    sample: dayjs().format("MM/YYYY"),
  },
  {
    key: "member_first_name",
    label: "Vorname erstes Mitglied",
    description: "Vorname des ersten Mitglieds im Vertrag",
    sample: "Max",
  },
  {
    key: "member_last_name",
    label: "Nachname erstes Mitglied",
    description: "Nachname des ersten Mitglieds im Vertrag",
    sample: "Mustermann",
  },
  {
    key: "member_payed_till",
    label: "Gezahlt bis (Mitglied)",
    description: "Aktueller Status \"bezahlt bis\" des ersten Mitglieds",
    sample: "08/2023",
  },
  {
    key: "member_names",
    label: "Mitgliedernamen",
    description: "Alle Mitgliedernamen des Vertrags",
    sample: "Mustermann Max, Musterfrau Anna",
  },
  {
    key: "contract_owner",
    label: "Kontoinhaber",
    description: "Hinterlegter Kontoinhaber des Vertrags",
    sample: "Familie Mustermann",
  },
  {
    key: "period_note",
    label: "Zeitraum",
    description: "Berechneter Zeitraum des Einzugs",
    sample: "09/2023-11/2023",
  },
  {
    key: "amount",
    label: "Betrag",
    description: "Gesamtsumme des Einzugs (formatiert)",
    sample: "120,00 €",
  },
  {
    key: "registration_note",
    label: "Hinweis Anmeldegebühr",
    description: "Text für ggf. enthaltene Anmeldegebühr",
    sample: "Anmeldung 20,00 €",
  },
  {
    key: "overdue_note",
    label: "Hinweis Rückstände",
    description: "Wird gesetzt, wenn Rückstände enthalten sind",
    sample: "inkl. Rückstände",
  },
];

const tokenResolver = {
  book_month: (ctx) => ctx.bookMonth ?? "",
  next_payed_till: (ctx) => ctx.nextPayedTill ?? "",
  member_first_name: (ctx) => ctx.firstMember?.firstName ?? "",
  member_last_name: (ctx) => ctx.firstMember?.lastName ?? "",
  member_payed_till: (ctx) => ctx.firstMember?.payedTill ?? "",
  member_names: (ctx) => ctx.memberNames ?? "",
  contract_owner: (ctx) => ctx.contractOwner ?? "",
  period_note: (ctx) => ctx.periodNote ?? "",
  amount: (ctx) => ctx.amountFormatted ?? "",
  registration_note: (ctx) => ctx.registrationNote ?? "",
  overdue_note: (ctx) => ctx.overdueNote ?? "",
};

const TOKEN_REGEX = /\{\{(.*?)\}\}/g;

function normalizeTemplateSpacing(template = "") {
  return template.replace(/\}\}\s*(?=\{\{)/g, "}} ");
}

export function renderRemittanceTemplate(template = "", context = {}) {
  if (!template) return "";

  const normalized = normalizeTemplateSpacing(template);

  return normalized.replace(TOKEN_REGEX, (match, rawKey) => {
    const key = String(rawKey || "").trim();
    const resolver = tokenResolver[key];
    if (!resolver) return "";
    const value = resolver(context);
    return value == null ? "" : String(value);
  });
}

export function parseTemplateToSegments(template = "") {
  if (!template) return [];

  const segments = [];
  const regex = /(\{\{.*?\}\})/g;
  let lastIndex = 0;

  template.replace(regex, (match, _group, offset) => {
    if (offset > lastIndex) {
      segments.push({ type: "text", value: template.slice(lastIndex, offset) });
    }

    const key = match.slice(2, -2).trim();
    if (key) {
      segments.push({ type: "token", key });
    }

    lastIndex = offset + match.length;
    return match;
  });

  if (lastIndex < template.length) {
    segments.push({ type: "text", value: template.slice(lastIndex) });
  }

  return segments;
}

export function buildTemplateFromSegments(segments = []) {
  if (!Array.isArray(segments)) return "";
  const rawTemplate = segments
    .map((segment) => {
      if (!segment) return "";
      if (segment.type === "token" && segment.key) {
        return `{{${segment.key}}}`;
      }
      return segment.value || "";
    })
    .join("");

  return normalizeTemplateSpacing(rawTemplate);
}

export const SAMPLE_REM_CONTEXT = {
  bookMonth: dayjs().format("MM/YYYY"),
  nextPayedTill: dayjs().format("MM/YYYY"),
  firstMember: { firstName: "Max", lastName: "Mustermann", payedTill: "08/2023" },
  memberNames: "Mustermann Max, Musterfrau Anna",
  contractOwner: "Familie Mustermann",
  periodNote: "09/2023-11/2023",
  amountFormatted: "120,00 €",
  registrationNote: "Anmeldung 20,00 €",
  overdueNote: "inkl. Rückstände",
};

